import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { setAuthTokenGetter } from './api';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import SearchPage from './pages/SearchPage';
import DettaglioServizio from './pages/DettaglioServizio';
import ProfiloProvider from './pages/ProfiloProvider';
import ProviderDashboard from './pages/ProviderDashboard';
import CreaServizio from './pages/CreaServizio';
import ModificaServizio from './pages/ModificaServizio';
import Messaggi from './pages/Messaggi';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ProfiloModifica from './pages/ProfiloModifica';
import AdminDashboard from './pages/AdminDashboard';
import FornitoreDettaglio from './pages/FornitoreDettaglio';
import Blog from './pages/Blog';
import Articolo from './pages/Articolo';
import Privacy from './pages/Privacy';
import Piani from './pages/Piani';
import GuidaFornitori from './pages/GuidaFornitori';
import CookieBanner from './components/CookieBanner';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';
import useRuolo from './hooks/useRuolo';
import { ProfiloProvider as ProfiloContextProvider } from './context/ProfiloContext';

import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return <div className="loading-container">Caricamento...</div>;
  }

  if (!isSignedIn) {
    return <Navigate to="/accedi" replace />;
  }

  return children;
}

// Route riservata agli admin: richiede login + ruolo 'admin'.
function AdminRoute({ children }) {
  const { isSignedIn, isLoaded } = useAuth();
  const { ruolo, loading } = useRuolo();

  if (!isLoaded || loading) {
    return <div className="loading-container">Caricamento...</div>;
  }
  if (!isSignedIn) {
    return <Navigate to="/accedi" replace />;
  }
  if (ruolo !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Route da fornitore: richiede login e NON-admin. L'admin e' un ruolo puro
// (non ha profilo/schede proprie) → se finisce su una pagina da fornitore
// (dashboard, profilo, messaggi) lo mandiamo al suo Pannello Admin.
function ProviderRoute({ children }) {
  const { isSignedIn, isLoaded } = useAuth();
  const { ruolo, loading } = useRuolo();

  if (!isLoaded || loading) {
    return <div className="loading-container">Caricamento...</div>;
  }
  if (!isSignedIn) {
    return <Navigate to="/accedi" replace />;
  }
  if (ruolo === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function AuthTokenSync() {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(getToken);
  }, [getToken]);
  return null;
}

function App() {
  return (
    <ProfiloContextProvider>
    <div className="app-container">
      <ScrollToTop />
      <Analytics />
      <AuthTokenSync />
      <Navbar />
      <main className="main-content">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/cerca" element={<SearchPage />} />
          <Route path="/servizio/:providerSlug/:schedaSlug" element={<DettaglioServizio />} />
          <Route path="/servizio/:slug" element={<DettaglioServizio />} />
          <Route path="/provider/:slug" element={<ProfiloProvider />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Articolo />} />
          <Route path="/accedi/*" element={<SignInPage />} />
          <Route path="/registrazione/*" element={<SignUpPage />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/piani" element={<Piani />} />
          <Route path="/guida" element={<GuidaFornitori />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProviderRoute>
                <ProviderDashboard />
              </ProviderRoute>
            }
          />
          <Route
            path="/dashboard/nuovo-servizio"
            element={
              <ProviderRoute>
                <CreaServizio />
              </ProviderRoute>
            }
          />
          <Route
            path="/dashboard/servizio/:id/modifica"
            element={
              <ProtectedRoute>
                <ModificaServizio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/messaggi"
            element={
              <ProviderRoute>
                <Messaggi />
              </ProviderRoute>
            }
          />
          <Route
            path="/dashboard/profilo"
            element={
              <ProviderRoute>
                <ProfiloModifica />
              </ProviderRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/fornitori/:id"
            element={
              <AdminRoute>
                <FornitoreDettaglio />
              </AdminRoute>
            }
          />

          {/* 404 Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
    </div>
    </ProfiloContextProvider>
  );
}

export default App;
